import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  Search,
  FileText,
  Clock,
  User,
  Activity,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  AlertCircle,
  Users,
  CreditCard,
  Building2,
  LogIn,
  LogOut,
  Settings,
  Edit,
  Trash,
  Plus,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

// Types
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string | null;
  userType: string | null;
  userRole: string | null;
  userEmail: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  organizationId: string | null;
  businessUnitId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
  // Enhanced fields
  userName?: string;
  organizationName?: string;
  businessUnitName?: string;
  resourceName?: string;
  description?: string;
  changesSummary?: string;
  changedFields?: string[];
}

interface AuditLogStats {
  totalLogs: number;
  todayLogs: number;
  uniqueActors: number;
  actionBreakdown: { action: string; count: number }[];
  resourceTypeBreakdown: { resourceType: string; count: number }[];
}

interface AuditLogResponse {
  logs: AuditLog[];
  total: number;
}

// Action icon mapping
function getActionIcon(action: string) {
  const actionLower = action.toLowerCase();
  if (actionLower.includes("login")) return LogIn;
  if (actionLower.includes("logout")) return LogOut;
  if (actionLower.includes("create") || actionLower.includes("add")) return Plus;
  if (actionLower.includes("update") || actionLower.includes("edit")) return Edit;
  if (actionLower.includes("delete") || actionLower.includes("remove")) return Trash;
  if (actionLower.includes("impersonat")) return Eye;
  if (actionLower.includes("customer")) return Users;
  if (actionLower.includes("card")) return CreditCard;
  if (actionLower.includes("admin") || actionLower.includes("tenant")) return Building2;
  if (actionLower.includes("settings") || actionLower.includes("config")) return Settings;
  return Activity;
}

// Action color mapping
function getActionColor(action: string): { bg: string; text: string; border: string } {
  const actionLower = action.toLowerCase();
  if (actionLower.includes("login") || actionLower.includes("logout")) {
    return { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", border: "border-blue-500/20" };
  }
  if (actionLower.includes("create") || actionLower.includes("add")) {
    return { bg: "bg-green-500/10", text: "text-green-600 dark:text-green-400", border: "border-green-500/20" };
  }
  if (actionLower.includes("update") || actionLower.includes("edit")) {
    return { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", border: "border-amber-500/20" };
  }
  if (actionLower.includes("delete") || actionLower.includes("remove")) {
    return { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", border: "border-red-500/20" };
  }
  if (actionLower.includes("impersonat")) {
    return { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", border: "border-purple-500/20" };
  }
  return { bg: "bg-slate-500/10", text: "text-slate-600 dark:text-slate-400", border: "border-slate-500/20" };
}

// Format timestamp
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

// Format relative time
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatTimestamp(timestamp);
}

// Log detail dialog
function LogDetailDialog({ log }: { log: AuditLog }) {
  const ActionIcon = getActionIcon(log.action);
  const colors = getActionColor(log.action);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Eye className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", colors.bg, colors.border, "border")}>
              <ActionIcon className={cn("h-5 w-5", colors.text)} />
            </div>
            <div className="flex-1">
              <p className="text-lg font-semibold">{log.description || log.action}</p>
              <p className="text-sm text-muted-foreground font-normal">
                {formatTimestamp(log.timestamp)}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          {/* Summary Card */}
          <div className="p-4 rounded-lg bg-muted/50 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Actor</p>
                <p className="text-sm font-medium">{log.userName || log.userEmail || "System"}</p>
                {log.userRole && (
                  <Badge variant="outline" className="text-xs mt-1">
                    {log.userRole}
                  </Badge>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Action Type</p>
                <p className="text-sm font-medium">{log.action}</p>
              </div>
              {log.resourceName && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Resource</p>
                  <p className="text-sm font-medium">{log.resourceName}</p>
                  <p className="text-xs text-muted-foreground">{log.resourceType}</p>
                </div>
              )}
              {log.ipAddress && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">IP Address</p>
                  <p className="text-sm font-mono">{log.ipAddress}</p>
                </div>
              )}
            </div>
            {log.organizationName && (
              <div className="space-y-1 pt-2 border-t">
                <p className="text-xs font-medium text-muted-foreground">Organization</p>
                <p className="text-sm">{log.organizationName}</p>
                {log.businessUnitName && (
                  <p className="text-xs text-muted-foreground">
                    Business Unit: {log.businessUnitName}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Changes Summary */}
          {log.changedFields && log.changedFields.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Changed Fields</p>
                <Badge variant="secondary" className="text-xs">
                  {log.changedFields.length} field{log.changedFields.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {log.changedFields.map((field) => (
                  <Badge key={field} variant="outline" className="font-mono text-xs">
                    {field}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* User Agent */}
          {log.userAgent && (
            <div className="space-y-2">
              <p className="text-sm font-medium">User Agent</p>
              <p className="text-xs font-mono bg-muted p-3 rounded-md overflow-x-auto">
                {log.userAgent}
              </p>
            </div>
          )}

          {/* Side-by-side diff for changes */}
          {log.oldValues && log.newValues && Object.keys(log.oldValues).length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Detailed Changes</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Before
                  </p>
                  <ScrollArea className="h-48 rounded-md border bg-red-500/5">
                    <pre className="p-3 text-xs font-mono text-red-600 dark:text-red-400">
                      {JSON.stringify(log.oldValues, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    After
                  </p>
                  <ScrollArea className="h-48 rounded-md border bg-green-500/5">
                    <pre className="p-3 text-xs font-mono text-green-600 dark:text-green-400">
                      {JSON.stringify(log.newValues, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>
              </div>
            </div>
          )}

          {/* Technical Details */}
          <div className="pt-4 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Technical Details</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Log ID:</span>
                <span className="font-mono">{log.id}</span>
              </div>
              {log.userId && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User ID:</span>
                  <span className="font-mono">{log.userId}</span>
                </div>
              )}
              {log.resourceId && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Resource ID:</span>
                  <span className="font-mono">{log.resourceId}</span>
                </div>
              )}
              {log.organizationId && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Org ID:</span>
                  <span className="font-mono">{log.organizationId}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditLogPage() {
  // Filter state
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const pageSize = 25;

  // Fetch audit logs
  const { data: logsData, isLoading: logsLoading, refetch } = useQuery<AuditLogResponse>({
    queryKey: ["audit-logs", search, actionFilter, resourceTypeFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (actionFilter !== "all") params.set("action", actionFilter);
      if (resourceTypeFilter !== "all") params.set("resourceType", resourceTypeFilter);
      params.set("limit", pageSize.toString());
      params.set("offset", (page * pageSize).toString());

      const response = await apiRequest("GET", `/api/admin/audit-logs?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch audit logs");
      return response.json();
    },
  });

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery<AuditLogStats>({
    queryKey: ["audit-logs-stats"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/audit-logs/stats");
      if (!response.ok) throw new Error("Failed to fetch audit log stats");
      return response.json();
    },
  });

  const totalPages = Math.ceil((logsData?.total || 0) / pageSize);

  // Get unique actions for filter dropdown
  const uniqueActions = stats?.actionBreakdown.map(a => a.action) || [];
  const uniqueResourceTypes = stats?.resourceTypeBreakdown.map(r => r.resourceType) || [];

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-display font-bold tracking-tight">Audit Log</h1>
          <Badge variant="premium" className="gap-1">
            <Shield className="h-3 w-3" />
            Admin Only
          </Badge>
        </div>
        <p className="text-muted-foreground">
          System-wide activity log tracking all administrative actions, user events, and security-relevant changes.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          {
            title: "Total Logs",
            value: stats?.totalLogs ?? 0,
            icon: FileText,
            color: "text-blue-500",
            bgColor: "bg-blue-500/10",
          },
          {
            title: "Today's Activity",
            value: stats?.todayLogs ?? 0,
            icon: Clock,
            color: "text-green-500",
            bgColor: "bg-green-500/10",
          },
          {
            title: "Unique Actors",
            value: stats?.uniqueActors ?? 0,
            icon: User,
            color: "text-purple-500",
            bgColor: "bg-purple-500/10",
          },
          {
            title: "Action Types",
            value: uniqueActions.length,
            icon: Zap,
            color: "text-amber-500",
            bgColor: "bg-amber-500/10",
          },
        ].map((stat, index) => (
          <Card key={stat.title} className="animate-reveal-up" style={{ animationDelay: `${index * 0.1}s` }}>
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className={cn("p-3 rounded-xl", stat.bgColor)}>
                  <stat.icon className={cn("h-5 w-5", stat.color)} />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  {statsLoading ? (
                    <Skeleton className="h-7 w-16 mt-1" />
                  ) : (
                    <p className="text-2xl font-display font-bold">{stat.value.toLocaleString()}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Activity Breakdown */}
      {stats && (stats.actionBreakdown.length > 0 || stats.resourceTypeBreakdown.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Action Breakdown */}
          {stats.actionBreakdown.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Top Actions</CardTitle>
                <CardDescription>Most frequent activity types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.actionBreakdown.slice(0, 5).map((item) => {
                    const colors = getActionColor(item.action);
                    const ActionIcon = getActionIcon(item.action);
                    const percentage = stats.totalLogs > 0 ? (item.count / stats.totalLogs) * 100 : 0;
                    return (
                      <div key={item.action} className="flex items-center gap-3">
                        <div className={cn("p-1.5 rounded-md", colors.bg)}>
                          <ActionIcon className={cn("h-3.5 w-3.5", colors.text)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium truncate">{item.action}</span>
                            <span className="text-xs text-muted-foreground">{item.count}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className={cn("h-full rounded-full transition-all", colors.bg.replace("/10", "/50"))}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resource Type Breakdown */}
          {stats.resourceTypeBreakdown.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Resource Types</CardTitle>
                <CardDescription>Activity by resource category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.resourceTypeBreakdown.slice(0, 5).map((item) => {
                    const percentage = stats.totalLogs > 0 ? (item.count / stats.totalLogs) * 100 : 0;
                    return (
                      <div key={item.resourceType} className="flex items-center gap-3">
                        <div className="p-1.5 rounded-md bg-primary/10">
                          <Activity className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium truncate">{item.resourceType}</span>
                            <span className="text-xs text-muted-foreground">{item.count}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary/50 transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters & Log Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Activity Log</CardTitle>
                <CardDescription>
                  {logsData?.total.toLocaleString() || 0} total entries
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search actions, resources..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={actionFilter}
              onValueChange={(value) => {
                setActionFilter(value);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {uniqueActions.map((action) => (
                  <SelectItem key={action} value={action}>
                    {action}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={resourceTypeFilter}
              onValueChange={(value) => {
                setResourceTypeFilter(value);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {uniqueResourceTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Timeline */}
          {logsLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : logsData?.logs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-1">No audit logs found</h3>
              <p className="text-sm text-muted-foreground">
                {search || actionFilter !== "all" || resourceTypeFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Audit logs will appear here as system activity occurs"}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {logsData?.logs.map((log, index) => {
                  const ActionIcon = getActionIcon(log.action);
                  const colors = getActionColor(log.action);
                  const userName = log.userName || log.userEmail || "System";
                  const initials = userName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);

                  return (
                    <div
                      key={log.id}
                      className="group relative animate-reveal-up"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {/* Timeline connector */}
                      {index !== logsData.logs.length - 1 && (
                        <div className="absolute left-[27px] top-14 w-0.5 h-full -mb-4 bg-gradient-to-b from-border to-transparent" />
                      )}

                      {/* Card */}
                      <div className={cn(
                        "relative flex gap-4 p-4 rounded-lg border bg-card transition-all duration-200",
                        "hover:shadow-md hover:border-primary/20 hover:bg-accent/5"
                      )}>
                        {/* Avatar/Icon Section */}
                        <div className="flex flex-col items-center gap-2 flex-shrink-0">
                          {/* User Avatar */}
                          <div className={cn(
                            "relative flex items-center justify-center w-12 h-12 rounded-full font-semibold text-sm",
                            "ring-2 ring-background shadow-sm transition-transform group-hover:scale-105",
                            colors.bg,
                            colors.text
                          )}>
                            {initials}
                            {/* Action Badge */}
                            <div className={cn(
                              "absolute -bottom-1 -right-1 flex items-center justify-center w-6 h-6 rounded-full border-2 border-background",
                              colors.bg
                            )}>
                              <ActionIcon className={cn("h-3 w-3", colors.text)} />
                            </div>
                          </div>
                        </div>

                        {/* Content Section */}
                        <div className="flex-1 min-w-0 space-y-3">
                          {/* Header: Description + Timestamp */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-base font-semibold leading-tight mb-1">
                                {log.description || log.action}
                              </h4>
                              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1.5">
                                  <User className="h-3.5 w-3.5" />
                                  <span className="font-medium">{userName}</span>
                                  {log.userRole && (
                                    <Badge variant="outline" className="text-xs h-5 px-1.5">
                                      {log.userRole}
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-muted-foreground/50">•</span>
                                <div className="flex items-center gap-1.5">
                                  <Clock className="h-3.5 w-3.5" />
                                  <span>{formatRelativeTime(log.timestamp)}</span>
                                </div>
                                {log.ipAddress && (
                                  <>
                                    <span className="text-muted-foreground/50">•</span>
                                    <span className="font-mono text-xs">{log.ipAddress}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatTimestamp(log.timestamp)}
                              </span>
                              <LogDetailDialog log={log} />
                            </div>
                          </div>

                          {/* Metadata: Resource + Changes */}
                          {(log.resourceName || log.organizationName || log.changesSummary) && (
                            <div className="flex flex-col gap-2 text-sm">
                              {/* Resource and Organization in one row */}
                              {(log.resourceName || log.organizationName) && (
                                <div className="flex flex-wrap items-center gap-2">
                                  {log.resourceName && (
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/50">
                                      <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span className="text-xs font-medium text-muted-foreground">
                                        {log.resourceType?.replace('_', ' ')}:
                                      </span>
                                      <span className="text-xs font-semibold">{log.resourceName}</span>
                                    </div>
                                  )}
                                  {log.organizationName && (
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/50">
                                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span className="text-xs font-semibold">{log.organizationName}</span>
                                      {log.businessUnitName && (
                                        <>
                                          <span className="text-muted-foreground/50">/</span>
                                          <span className="text-xs">{log.businessUnitName}</span>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Changes in full-width row */}
                              {log.changesSummary && (
                                <div className="flex items-start gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20">
                                  <Edit className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-foreground mb-1">
                                      {log.changedFields?.length || 0} change{log.changedFields && log.changedFields.length !== 1 ? 's' : ''}:
                                    </div>
                                    {(() => {
                                      const changes = log.changesSummary.split(', ');
                                      if (changes.length === 1) {
                                        return (
                                          <div className="text-xs text-foreground/80">
                                            {log.changesSummary}
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="space-y-1">
                                          {changes.map((change, idx) => (
                                            <div key={idx} className="text-xs text-foreground/80">
                                              • {change.trim()}
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, logsData?.total || 0)} of{" "}
                    {logsData?.total.toLocaleString()} entries
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
